
import { BRAND_COLLECTIONS, collectionPath } from "@/utils/collections";
// How long a model flyout stays open after the pointer leaves its product, so a
// diagonal move from the product name onto the flyout doesn't drop it.
const MODELS_CLOSE_DELAY = 300;

export default {
  methods: {
    collectionPath,

    openModels(item, event) {
      if (!item.models || this.isDismissingModels) return;

      clearTimeout(this.modelsCloseTimer);
      if (this.openModelsId === item.id) return;

      this.openModelsId = item.id;
      this.openModelsRow = event.currentTarget;
      this.placeModels();
    },

    // Placed against the lists column rather than inside its row: the brands
    // block scrolls on short screens (overflow-y: auto), and anything positioned
    // within it would be clipped at its edge.
    placeModels() {
      const row = this.openModelsRow.getBoundingClientRect();
      const lists = this.$refs.lists.getBoundingClientRect();

      this.$set(this.modelsPositions, this.openModelsId, {
        top: row.top - lists.top + "px",
        left: row.right - lists.left + "px",
      });
    },

    // Which also means following the row by hand when that block scrolls, and
    // closing once the row is out of sight.
    followModelsRow(event) {
      if (!this.openModelsId) return;

      const row = this.openModelsRow.getBoundingClientRect();
      const block = event.currentTarget.getBoundingClientRect();

      if (row.bottom <= block.top || row.top >= block.bottom) {
        this.openModelsId = null;
        return;
      }

      this.placeModels();
    },

    closeModelsLater(item) {
      if (!item.models) return;

      clearTimeout(this.modelsCloseTimer);
      this.modelsCloseTimer = setTimeout(() => {
        if (this.openModelsId === item.id) this.openModelsId = null;
      }, MODELS_CLOSE_DELAY);
    },

    handleModelsFocusOut(item, event) {
      if (!item.models || event.currentTarget.contains(event.relatedTarget))
        return;

      if (this.openModelsId === item.id) this.openModelsId = null;
    },

    // Escape from inside the flyout puts focus back on the product link, and
    // that focus must not reopen what Escape just closed.
    dismissModels(item, event) {
      if (this.openModelsId !== item.id) return;

      this.openModelsId = null;
      this.isDismissingModels = true;
      event.currentTarget.querySelector("a").focus();
      this.isDismissingModels = false;
    },

    handleModelClick(item, model) {
      this.openModelsId = null;
      this.$emit("modelClick", item, model);
    },
  },

  props: {
    superInventoryItems: {
      type: Array,
      required: true,
    },
    photo: {
      type: String,
      required: false,
    },
    currentCategoryTreeId: {
      type: String,
      required: false,
    },
    brandGroups: {
      type: Array,
      required: false,
      default: () => [],
    },
    scrollLists: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      BRAND_COLLECTIONS,
      activePhoto: null,
      staticItemsPerColumn: 6,
      dynamicItemsPerColumn: 8,
      openModelsId: null,
      openModelsRow: null,
      // Per product, so a flyout fading out keeps its place while the next
      // one opens elsewhere.
      modelsPositions: {},
      modelsCloseTimer: null,
      isDismissingModels: false,
    };
  },
  computed: {
    filteredItems() {
      return this.superInventoryItems.filter((item) => {
        return !item.private || (item.private === true && this.__isLoggedIn);
      });
    },
    staticItems() {
      return this.filteredItems.filter((item) =>
        item.hasOwnProperty("private"),
      );
    },
    dynamicItems() {
      return this.filteredItems.filter(
        (item) => !item.hasOwnProperty("private"),
      );
    },
    staticColumns() {
      // An explicit `column` on any static item means the tab has hand-placed
      // its links (Saunas does — see navigationItems.vue) and chunking by count
      // would scramble them. Empty columns are dropped so a column that only
      // held logged-in-only links does not leave a gap for guests.
      const pinned = this.staticItems.filter((item) => item.column);

      if (pinned.length) {
        const byColumn = new Map();
        for (const item of this.staticItems) {
          const key = item.column || 1;
          if (!byColumn.has(key)) byColumn.set(key, []);
          byColumn.get(key).push(item);
        }
        return [...byColumn.keys()]
          .sort((a, b) => a - b)
          .map((key) => byColumn.get(key));
      }

      const columns = [];
      for (
        let i = 0;
        i < this.staticItems.length;
        i += this.staticItemsPerColumn
      ) {
        columns.push(this.staticItems.slice(i, i + this.staticItemsPerColumn));
      }
      return columns;
    },
    dynamicColumns() {
      const columns = [];
      for (
        let i = 0;
        i < this.dynamicItems.length;
        i += this.dynamicItemsPerColumn
      ) {
        columns.push(
          this.dynamicItems.slice(i, i + this.dynamicItemsPerColumn),
        );
      }
      return columns;
    },
    hasBrandGroups() {
      return this.brandGroups && this.brandGroups.length > 0;
    },
    photoToShow() {
      return this.activePhoto || this.photo;
    },
    limitedDynamicItemsCol1() {
      return this.dynamicItems.slice(0, 6);
    },
    limitedDynamicItemsCol2() {
      return this.dynamicItems.slice(6, 10);
    },
  },
  watch: {
    superInventoryItems() {
      this.activePhoto = null;
      this.openModelsId = null;
    },
  },

  beforeDestroy() {
    clearTimeout(this.modelsCloseTimer);
  },
};
